/**
 * "Impayés" tab content for the mobile Staff view (Story 11.6-3).
 *
 * Lists ALL company unpaid orders (not scoped to the caller — locked decision, see story
 * Context & decisions) via the existing GET /api/orders?payment_status=unpaid|overdue endpoint
 * (no new backend endpoint). Kept in its own small component so StaffView.tsx (already large)
 * doesn't grow further — mirrors the per-tab lazy-mount pattern already used for the "sales" tab
 * (StaffView only renders this component while activeTab === "unpaid").
 *
 * Reuses PaymentBadge + orderPayment.ts helpers (canMarkOrderPaid, paymentDaysOverdue) so the
 * mobile tab can't drift from the desktop OrderDetail/Orders payment UI (11.6-1 / 11.6-2).
 *
 * i18n: new strings live in the `staff` namespace (t() default ns here). The due-date line reuses
 * the existing `orders:payment_due_on` string via an explicit `orders:` prefix — this is the exact
 * regression guard called out by the 11.6-1 bug (keys referenced from `staff` but defined only in
 * `orders` silently render the raw key); an explicit cross-namespace prefix is the sanctioned way
 * to reuse a string that genuinely lives in `orders`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Card } from "../../components/molecules/Card";
import { Button } from "../../components/atoms/Button";
import { PaymentBadge } from "../../components/molecules/PaymentBadge";
import { getOrders, markOrderPayment } from "../../services/orderService";
import { useAuthStore } from "../../store/authStore";
import { canMarkOrderPaid, paymentDaysOverdue } from "../../utils/orderPayment";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatCurrency } from "../../utils/currency";
import { formatDate } from "../../utils/date";
import { useDebounce } from "../../hooks/useDebounce";
import type { OrderListItem } from "../../types/models";

/** Open Question 3 default: 20 (matches the orders list endpoint's own default `limit`). */
const PAGE_SIZE = 20;

type UnpaidSubFilter = "unpaid" | "overdue";

interface UnpaidOrdersTabProps {
  /** Reuses StaffView's single toast banner instead of duplicating toast UI. */
  onToast: (message: string, type: "success" | "error") => void;
}

/**
 * Overdue-first, then ascending due date (rows without a due date sort last).
 * Open Question 2 default: `list_orders` only supports `order_by(updated_at desc)` — no
 * due-date ordering server-side — so this sorts the fetched page client-side, per the story's
 * own documented fallback. Overdue-ness reuses `paymentDaysOverdue` (orderPayment.ts) so the
 * sort can't disagree with the badge it's ordered around.
 */
function sortUnpaid(items: OrderListItem[]): OrderListItem[] {
  return [...items].sort((a, b) => {
    const aOverdue = a.payment_due_date ? paymentDaysOverdue(a.payment_due_date) >= 1 : false;
    const bOverdue = b.payment_due_date ? paymentDaysOverdue(b.payment_due_date) >= 1 : false;
    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
    if (!a.payment_due_date && !b.payment_due_date) return 0;
    if (!a.payment_due_date) return 1;
    if (!b.payment_due_date) return -1;
    return a.payment_due_date.localeCompare(b.payment_due_date);
  });
}

export function UnpaidOrdersTab({ onToast }: UnpaidOrdersTabProps) {
  const { t } = useTranslation("staff");
  const { user } = useAuthStore();

  const [subFilter, setSubFilter] = useState<UnpaidSubFilter>("unpaid");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);

  const [items, setItems] = useState<OrderListItem[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);
  // Monotonic request id: guards against out-of-order responses when the sub-filter is
  // toggled or the search box is edited faster than the network resolves (a slower earlier
  // request must not overwrite a newer one, or the list would disagree with the active filter).
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await getOrders({
          payment_status: subFilter,
          search: debouncedSearch || null,
          page: pageNum,
          limit: PAGE_SIZE,
        });
        if (requestId !== requestIdRef.current) return; // superseded by a newer request
        setItems((prev) => sortUnpaid(append ? [...prev, ...res.items] : res.items));
        setPage(res.page);
        setPages(res.pages);
      } catch (e) {
        if (requestId !== requestIdRef.current) return; // superseded — swallow the stale error
        setError(getApiErrorMessage(e, t("unpaid_error")));
        if (!append) setItems([]);
      } finally {
        // Only the latest request owns the loading flags (a stale one must not clear them).
        if (requestId === requestIdRef.current) {
          if (append) setLoadingMore(false);
          else setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subFilter, debouncedSearch]);

  const handleMarkPaid = async (order: OrderListItem) => {
    setMarkingId(order.id);
    try {
      await markOrderPayment(order.id, { payment_status: "paid" });
      setItems((prev) => prev.filter((o) => o.id !== order.id));
      onToast(t("unpaid_marked_toast", { orderNumber: order.order_number }), "success");
    } catch (e) {
      onToast(getApiErrorMessage(e, t("unpaid_mark_paid_error")), "error");
    } finally {
      setMarkingId(null);
    }
  };

  const hasMore = page < pages;

  return (
    <div className="space-y-4">
      {/* Sub-filter (Tous impayés / En retard) + customer search (AC2) */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <nav
          className="inline-flex rounded-lg border border-neutral-200 dark:border-neutral-700 p-0.5 w-fit"
          aria-label="Payment filter"
        >
          <button
            type="button"
            onClick={() => setSubFilter("unpaid")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md min-h-[44px] ${
              subFilter === "unpaid"
                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            }`}
          >
            {t("unpaid_filter_all")}
          </button>
          <button
            type="button"
            onClick={() => setSubFilter("overdue")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md min-h-[44px] ${
              subFilter === "overdue"
                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            }`}
          >
            {t("unpaid_filter_overdue")}
          </button>
        </nav>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("unpaid_search_placeholder")}
            aria-label={t("unpaid_search_placeholder")}
            className="w-full ps-10 pe-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 min-h-[44px]"
          />
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-8 text-center">
          <p className="text-neutral-600 dark:text-neutral-400">{t("unpaid_loading")}</p>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-red-800 dark:text-red-200">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-8 text-center">
          <p className="text-neutral-600 dark:text-neutral-400 font-medium">{t("unpaid_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((order) => {
            const canMark = canMarkOrderPaid(order, user?.role, user?.id);
            return (
              <Card key={order.id} variant="default" padding="md">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="font-mono font-semibold text-neutral-900 dark:text-neutral-100">
                      {order.order_number}
                    </span>
                    <p className="mt-0.5 font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {order.customer_name}
                    </p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {formatCurrency(Number(order.total_amount))}
                    </p>
                    {order.payment_due_date && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-0.5">
                        {t("orders:payment_due_on", { date: formatDate(order.payment_due_date) })}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <PaymentBadge order={order} size="sm" />
                    {canMark && (
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        loading={markingId === order.id}
                        onClick={() => handleMarkPaid(order)}
                      >
                        {t("unpaid_mark_paid")}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {hasMore && !loading && !error && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={loadingMore}
            onClick={() => fetchPage(page + 1, true)}
          >
            {t("unpaid_load_more")}
          </Button>
        </div>
      )}
    </div>
  );
}
